'use client';

import { useEffect } from 'react';
import { Locale, obfuscateLinks } from '@headless-workspace/config';
import { Component } from '@headless-workspace/typings';
import { IsomorphicLogger } from '@headless-workspace/utils';
import { DataLayerClassName, DataLayerEventLabel, DataLayerVendorsConsent, useDataLayer } from '../../../client';
import { useCarouselTracking } from './hooks/useCarouselTracking';
import { useEventListeners } from './hooks/useEventListeners';
import { useHeaderDataLayer } from './hooks/useHeaderDataLayer';
import { useHeaderEventHandlers } from './hooks/useHeaderEventHandlers';

type HeaderDidMountProps = {
    dataLayerConnectionAction: string;
    trackEvent: ReturnType<typeof useDataLayer>['trackEvent'];
    trackT2SEvent?: ReturnType<typeof useDataLayer>['trackT2SEvent'];
    locale: Locale;
    addVariables: ReturnType<typeof useDataLayer>['addVariables'];
    deleteVariable: ReturnType<typeof useDataLayer>['deleteVariable'];
};

// Safe wrapper for window.cact
const safeCact = (action: string, callback: (result: DataLayerVendorsConsent) => void) => {
    if (typeof window !== 'undefined' && typeof window.cact === 'function') {
        try {
            window.cact(action, callback);
        } catch (error) {
            new IsomorphicLogger().error('Error calling cact:', error);
        }
    } else {
        new IsomorphicLogger().warn('cact is not available');
    }
};

export const HeaderDidMount: Component<HeaderDidMountProps> = ({
    dataLayerConnectionAction,
    trackEvent,
    locale,
    addVariables,
    trackT2SEvent,
    deleteVariable,
}) => {
    const { userStatus, isSessionInitialized, setDataLayerPotionGuestId, managePotionGuestId } = useHeaderDataLayer({
        addVariables,
        deleteVariable,
        locale,
    });
    const {
        connectionEventClick,
        t2SProductEventClick,
        connectionBannerEventClick,
        categoryEventClick,
        nestedCategoryEventClick,
        persistentBannerEventClick,
    } = useHeaderEventHandlers({ trackEvent, trackT2SEvent, dataLayerConnectionAction });

    // Carousel tracking
    useCarouselTracking({ trackEvent, t2SProductEventClick });

    useEffect(() => {
        if (typeof document !== 'undefined') {
            obfuscateLinks();
        }
    }, []);

    const eventListenerConfigs = [
        {
            selector: `.${DataLayerClassName.connection}`,
            handler: () => connectionEventClick,
        },
        {
            selector: `.${DataLayerClassName.burgerMenuItem}`,
            handler: (element: Element) => {
                const dataLayerLabel = element.getAttribute('data-layer-label') ?? DataLayerEventLabel.Null;
                return connectionBannerEventClick(dataLayerLabel);
            },
        },
        {
            selector: `.${DataLayerClassName.categoryNavigation}`,
            handler: (element: Element) => {
                const dataLayerId = element.getAttribute('data-layer-id') ?? DataLayerEventLabel.Null;
                return categoryEventClick(dataLayerId);
            },
        },
        {
            selector: `.${DataLayerClassName.nestedCategoryNavigation}`,
            handler: (element: Element) => {
                const dataLayerId = element.getAttribute('data-layer-id') ?? DataLayerEventLabel.Null;
                const dataLayerSubId = element.getAttribute('data-layer-sub-id') ?? DataLayerEventLabel.Null;
                return nestedCategoryEventClick(dataLayerId, dataLayerSubId);
            },
        },
        {
            selector: `.${DataLayerClassName.persistentBanner}`,
            handler: (element: Element) => {
                const dataLayerId = element.getAttribute('data-layer-id') ?? DataLayerEventLabel.Null;
                return persistentBannerEventClick(dataLayerId);
            },
        },
    ];

    useEventListeners(eventListenerConfigs, [
        categoryEventClick,
        connectionEventClick,
        nestedCategoryEventClick,
        persistentBannerEventClick,
    ]);

    useEventListeners(
        [
            {
                selector: `.${DataLayerClassName.connectionBanner}`,
                handler: (element: Element) => {
                    const dataLayerLabel = element.getAttribute('data-layer-label') ?? DataLayerEventLabel.Null;
                    return connectionBannerEventClick(dataLayerLabel);
                },
            },
        ],
        [connectionBannerEventClick, userStatus],
    );

    useEffect(() => {
        if (isSessionInitialized) {
            setDataLayerPotionGuestId();
        }

        safeCact('consent.onUpdate', managePotionGuestId);
    }, [isSessionInitialized, setDataLayerPotionGuestId, managePotionGuestId]);

    return null;
};
